import React from "react";
import { cn } from "../utils/cn";

export interface LogoLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Size of the logo in pixels (width and height).
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl" | number;

  /**
   * Custom loading text to display below the logo.
   */
  text?: string;

  /**
   * Whether to show the default text "Loading..." if no custom text is provided.
   * @default false
   */
  showText?: boolean;

  /**
   * Animation style of the logo.
   * @default "pulse"
   */
  variant?: "pulse" | "spin" | "bounce" | "ping" | "none";
}

const sizeMap = {
  sm: "w-8 h-8",
  md: "w-16 h-16",
  lg: "w-32 h-32",
  xl: "w-48 h-48",
};

const variantMap = {
  pulse: "animate-pulse",
  spin: "animate-spin",
  bounce: "animate-bounce",
  ping: "animate-ping",
  none: "",
};

export function LogoLoader({
  className,
  size = "md",
  text,
  showText = false,
  variant = "bounce",
  ...props
}: LogoLoaderProps) {
  const sizeClass =
    typeof size === "number"
      ? `w-[${size}px] h-[${size}px]`
      : sizeMap[size] || sizeMap.md;

  const imageStyle =
    typeof size === "number" ? { width: size, height: size } : undefined;

  const animationClass = variantMap[variant] || variantMap.pulse;
  const displayText = text ?? (showText ? "잠시만 기다려주세요..." : null);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        className,
      )}
      {...props}
    >
      <div
        className={cn("relative flex items-center justify-center", sizeClass)}
      >
        {/* Main Logo with Animation Effect */}
        <img
          src="/brand/semo-logo.svg"
          alt="로딩 중"
          className={cn("w-full h-full object-contain", animationClass)}
          style={imageStyle}
        />
      </div>

      {displayText && (
        <p className="text-muted-foreground text-sm font-medium animate-pulse text-center">
          {displayText}
        </p>
      )}
    </div>
  );
}
